import { Box, Stack } from "@mui/material";
import { useState } from "react";

import images from "../constants/images";
import { STEP_TUTORIAL, type StepTutorialTypes } from "../constants/tutorial";
import "../index.css";
import { moveValleyBlock } from "../store";
import type {
  ResourceObjects,
  TokenValleyBlockType,
} from "../types/tokenValley";
import BlockTimer from "./BlockTimer";
import DrawerBuildersHutHouse from "./DrawerBuildersHutHouse";
import DrawerEnterHouse from "./DrawerEnterHouse";
import ExtractionIcon from "./ExtractionIcon";
import IconBlock from "./IconBlock";
import { useTutorial } from "./hooks/useTutorial";
import AnimationBadge from "./ui/AnimationBadge";
import HandTutorial from "./ui/HandTutorial";

interface TypesProps {
  top: number;
  left: number;
  index: number;
  moveMode: boolean;
  playerDataStep: number;
  block: TokenValleyBlockType;
  stepTutorial: StepTutorialTypes;
  resourceGathering: ResourceObjects;
  resourceGatheringButton: ResourceObjects;
  setStepTutorial: (step: StepTutorialTypes) => void;
  activeBlock: TokenValleyBlockType | null;
  setResourceGathering: (resourceGathering: ResourceObjects) => void;
  setActiveBlock: (block: TokenValleyBlockType | null) => void;
}

const Block = ({
  top,
  left,
  index,
  block,
  moveMode,
  activeBlock,
  stepTutorial,
  playerDataStep,
  setActiveBlock,
  setStepTutorial,
  resourceGathering,
  setResourceGathering,
  resourceGatheringButton,
}: TypesProps) => {
  const [openSell, setOpenSell] = useState<boolean>(false);
  const [openBuildersHut, setOpenBuildersHut] = useState<boolean>(false);
  const { hideTutorial } = useTutorial();

  const handleClick = () => {
    if (playerDataStep === -2 && STEP_TUTORIAL.SELL) {
      hideTutorial();
    }
    if (playerDataStep !== -2) {
      if (
        stepTutorial === STEP_TUTORIAL.SELECT_BUILDING &&
        playerDataStep === 2
      ) {
        return;
      }
      if (block.block_position !== 21 && stepTutorial === STEP_TUTORIAL.INIT) {
        return;
      }
      if (block.block_position === 21 && stepTutorial === STEP_TUTORIAL.INIT) {
        setStepTutorial(STEP_TUTORIAL.CLICK_PLACE);
      }
      if (
        playerDataStep === 3 &&
        block.block_position !== 23 &&
        stepTutorial === STEP_TUTORIAL.COLLECT_RESOURCES
      ) {
        return;
      }
      if (
        playerDataStep === 3 &&
        block.block_position === 23 &&
        stepTutorial === STEP_TUTORIAL.COLLECT_RESOURCES
      ) {
        setStepTutorial(STEP_TUTORIAL.OPEN_SELL);
      }
    }

    if (block.block_type === "build/token_house" && !moveMode) {
      setOpenSell(true);
    }
    if (
      (block?.block_type === "build/builders_hut" && !moveMode) ||
      (block?.block_type === "build/void" && !moveMode)
    ) {
      setOpenBuildersHut(true);
    }
    if (!moveMode) {
      setActiveBlock(block);
    }
    if (moveMode) {
      if (block.block_type === "build/void" && !activeBlock) return;

      if (!activeBlock) {
        setActiveBlock(block);
      } else if (block.block_type === "build/void") {
        moveValleyBlock({
          original_position: activeBlock.block_position,
          new_position: block.block_position,
        });
        setActiveBlock(null);
      } else {
        setActiveBlock(block);
      }
    }
  };

  return (
    <>
      <Box position={"relative"} onClick={handleClick} mr={32}>
        <Box
          position={"absolute"}
          width={"62px"}
          zIndex={1}
          style={{
            top: `${top}px`,
            left: `${left}px`,
          }}
        >
          <Stack position="relative">
            {block.block_position === 21 &&
              stepTutorial === STEP_TUTORIAL.SELECT_BUILDING &&
              playerDataStep === 2 && (
                <HandTutorial right="-5px" bottom="-50px" />
              )}
            {block.block_position === 21 &&
              stepTutorial === STEP_TUTORIAL.INIT &&
              playerDataStep === 1 && (
                <HandTutorial bottom="-90px" right="0px" />
              )}
            {playerDataStep === 3 &&
              block.block_type === "build/token_house" &&
              stepTutorial === STEP_TUTORIAL.COLLECT_RESOURCES && (
                <HandTutorial right="-10px" bottom="-75px" />
              )}
          </Stack>
          {block.block_time_ready > new Date().getTime() && (
            <BlockTimer block={block} />
          )}

          <IconBlock
            blockType={block.block_type}
            shadow
            buildAnimation={block.block_time_ready > new Date().getTime()}
          />
          {block.block_production &&
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-expect-error
            Object.values(block.resource_stock)[0] >= 100 &&
            resourceGatheringButton !==
              Object.keys(block.block_production)[0] && (
              <ExtractionIcon
                block={block}
                playerDataStep={playerDataStep}
                setStepTutorial={setStepTutorial}
                resourceGathering={resourceGathering}
                setResourceGathering={setResourceGathering}
              />
            )}
          {block.block_production &&
            Object.values(block.resource_stock)[0] !== undefined &&
            resourceGatheringButton !== "resource/void" &&
            block.block_production[0].resource_name ===
              resourceGatheringButton &&
            block.block_type !== "build/token_house" &&
            !moveMode && <AnimationBadge block={block} />}
        </Box>
        <Box
          position={"absolute"}
          top={`${top}px`}
          left={`${left}px`}
          borderRadius={"5px"}
          width={"69px"}
          height={"69px"}
          sx={{
            transform: "rotateX(60deg) rotateZ(45deg)",
            backgroundImage: `url(${index % 2 === 0 ? images.darkBgBlock : images.lightBgBlock})`,
            backgroundSize: "300% 300%",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            filter:
              block.block_uuid === activeBlock?.block_uuid
                ? "brightness(1.2)"
                : "none",
          }}
        />
      </Box>

      <DrawerEnterHouse
        block={block}
        open={openSell}
        setOpen={setOpenSell}
        stepTutorial={stepTutorial}
        setStepTutorial={setStepTutorial}
      />
      <DrawerBuildersHutHouse
        open={openBuildersHut}
        activeBlock={activeBlock}
        stepTutorial={stepTutorial}
        setOpen={setOpenBuildersHut}
        playerDataStep={playerDataStep}
        setStepTutorial={setStepTutorial}
      />
    </>
  );
};

export default Block;
